import React, { useEffect, useMemo, useState } from 'react';
import { Toggle } from '../../../shared/toggle/toggle';
import { CategoryWithStatus } from 'src/dtos/category-with-status.dto';
import { useStores } from '../../../store';
import './category-list.scss';
import { observer } from 'mobx-react-lite';
import { useCategoryFiltersConfig } from '../../../shared/filters/filter-config/category-filters';
import { Filters } from '../../../shared/filters/filters';
import { BulkPolicyUpdate } from '../../../shared/bulk-options/bulk-policy-update';
import { BulkProtectionOptions, CategoriesFilterNames, WebFilteringSubMenu } from '../../../../utils/enums';
import { StatusBadge } from '../../../shared/status-badge/status-badge';
import { SystemConfiguration } from 'src/utils/system-configuration';
import { getCurrentFilterLabel } from 'src/utils/filters-util';
import { NoResult } from '../../../shared/no-result/no-result';
import Loader from '../../../shared/loader/loader';
import { ServiceItemTooltip } from '../../services/service-item-tooltip/service-item-tooltip';

export const CategoryList: React.FC = observer(() => {
    const {fwVersionStore} = useStores();
    const {categoryDtoList} = SystemConfiguration.getInstance();
    const [categoryListFiltered, setCategoryListFiltered] = useState<CategoryWithStatus[] | undefined>(undefined);
    const [blockCategoriesProfile, setBlockCategoriesProfile] = useState<string[]>([]);

    const categoryFiltersConfig = useCategoryFiltersConfig();

    useEffect(() => {
        fwVersionStore.setWebFilteringType(WebFilteringSubMenu.CATEGORIES);
    }, []);

    const categoryListOriginal: CategoryWithStatus[] = useMemo(() => {
        let categoryDtoListCopy: any = [...categoryDtoList];
        const blockCategoriesSelectedVersionStore = fwVersionStore.urlFilteringBlockCategoriesList;
        categoryDtoListCopy = categoryDtoListCopy.map(((category: any) =>
            ({ ...category, isAllow: !blockCategoriesSelectedVersionStore?.includes(category.categoryName) })));
        setBlockCategoriesProfile(blockCategoriesSelectedVersionStore);
        return categoryDtoListCopy;
    }, [fwVersionStore.selectedFwVersion]);

    const disableVersionReason = useMemo(() => fwVersionStore.getDisableReason, [fwVersionStore.selectedFwVersion]);

    const onChange = async (val: any, index: number) => {
        await fwVersionStore.setIsDirty();
        const originalData: CategoryWithStatus[] = structuredClone(categoryListFiltered) || [];
        const categoryMapCopy: CategoryWithStatus[] = structuredClone(categoryListFiltered) || [];
        categoryMapCopy[index].isAllow = val;
        setCategoryListFiltered(categoryMapCopy);
        let blockCategories = [...blockCategoriesProfile];
        const currentCategoryName = categoryMapCopy[index].categoryName;
        if (val) {
            blockCategories = blockCategories.filter(name => name !== currentCategoryName);
        } else {
            blockCategories.push(currentCategoryName);
        }
        try {
            await fwVersionStore.updateCategoryStatus(blockCategories);
        }
        catch (e) {
            setCategoryListFiltered(originalData);
        }
    };

    const isRecommended = useMemo(() => {
        return categoryListOriginal.every(c =>
            (c.blockedByDefault && !c.isAllow) || (!c.blockedByDefault && c.isAllow));
    }, [categoryListOriginal]);

    const filterLabel = getCurrentFilterLabel(categoryFiltersConfig.localStorageKey || '', categoryFiltersConfig.radioButtons?.options || []);
    const noResultsTitle = useMemo(() => {
        const subText = filterLabel === CategoriesFilterNames.HighRiskCategories ||
            filterLabel === CategoriesFilterNames.AICategories ? '' : 'Categories';
        return filterLabel === CategoriesFilterNames.All ?
            'No Search Result' :
            `No ${filterLabel} ${subText}`;
    }, [filterLabel]);


    const setDataFromFilter = (data: CategoryWithStatus[]) => {
        if(fwVersionStore.selectedFwVersion) {
            setCategoryListFiltered(data);
        }
    };

    const getProperDisplay = (data: CategoryWithStatus[] | undefined) => {
        if(!data) return null;
        if(data.length === 0) {
            return <NoResult title={noResultsTitle}/>;
        }
        return <div className="category-toggle-list">
            {data?.map((category, index) =>
                <div className="card" key={category.categoryTitle}>
                    <div className="toggle-name">{category.categoryTitle}</div>
                    <ServiceItemTooltip title={category.categoryTitle}
                                        descriptionList={[category.categoryDescription]} />
                    <Toggle
                            key={category.categoryTitle}
                            disableReason={disableVersionReason}
                            value={category.isAllow}
                            onChange={(val) => onChange(val, index)}
                            withIcon={true}/>
                </div>

            )}</div> ;
    };

    return <>
        <div className={'filter-wrapper'}>
            <Filters filterConfig={categoryFiltersConfig}
                     setData={setDataFromFilter}
                     originalData={categoryListOriginal}
            />
            <div className={'right-actions'}>
                { categoryListOriginal.length > 0 && <StatusBadge isRecommended={isRecommended} pageName={BulkProtectionOptions.WEB_FILTERING}/> }
                <BulkPolicyUpdate tab={BulkProtectionOptions.WEB_FILTERING}/>
            </div>
        </div>
        {(fwVersionStore.isLoading || (!categoryListFiltered && !categoryListOriginal) || !fwVersionStore.selectedFwVersion) && <Loader type="full-screen"/>}
        {getProperDisplay(categoryListFiltered)}
    </>;
});